import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { MENU_ITEMS } from "../data/site";
import { SpiceLevel, VegBadge, LeafDivider } from "../components/brand/Icons";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useReveal } from "../hooks/useReveal";

export default function MenuPage() {
  const rootRef = useReveal();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <div ref={rootRef} data-testid="menu-page" className="bg-cream min-h-screen">
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-32 md:pt-40 pb-14 md:pb-20 bg-cream grain">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/"
            data-testid="menu-back-home"
            className="inline-flex items-center gap-2 text-cocoa-soft hover:text-cocoa transition-colors text-sm"
          >
            <ArrowLeft size={16} /> Back to home
          </Link>

          <div className="mt-8 max-w-3xl reveal">
            <div className="text-[11px] uppercase tracking-[0.3em] text-gold font-semibold">
              The Full Menu
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-cocoa mt-4 leading-[1.02] tracking-tight">
              Six biryanis. One{" "}
              <span className="gold-underline">stubborn philosophy</span>.
            </h1>
            <p className="mt-6 text-cocoa-soft text-base md:text-lg leading-relaxed max-w-2xl">
              Every handi is layered, sealed and dum-cooked the same morning
              you eat it. Spice levels are calibrated to the recipe — we
              don&apos;t tone them down on request.
            </p>
          </div>

          <LeafDivider label="॥ Annapoorna ॥" />
        </div>
      </section>

      {/* Filters / Legend */}
      <section className="bg-cream-deep py-8 border-y border-cream-deep">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-wrap items-center gap-6 md:gap-10 text-sm">
          <div className="flex items-center gap-3">
            <span className="text-[11px] uppercase tracking-[0.24em] text-gold font-semibold">
              Spice
            </span>
            <SpiceLevel level={1} /> Mild
            <SpiceLevel level={2} /> Medium
            <SpiceLevel level={3} /> Andhra Heat
          </div>
          <div className="hidden md:block h-5 w-px bg-[#C8A14B]/40" />
          <div className="flex items-center gap-4">
            <VegBadge type="veg" />
            <VegBadge type="non-veg" />
          </div>
          <div className="ml-auto text-xs text-cocoa-soft italic">
            All biryanis served with raita, mirchi ka salan and onion-cucumber.
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {MENU_ITEMS.map((item, idx) => (
            <article
              key={item.id}
              data-testid={`menu-page-card-${item.id}`}
              className="reveal group bg-card-warm rounded-2xl border border-cream-deep overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 relative"
              style={{ transitionDelay: `${idx * 50}ms` }}
            >
              <div className="relative overflow-hidden h-56">
                <img
                  src={item.image}
                  alt={item.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute top-3 left-3 rounded-full bg-[#FAF3E7]/95 backdrop-blur px-3 py-1 shadow">
                  <VegBadge type={item.type} />
                </div>
              </div>

              <div className="p-6">
                <h2 className="font-display text-xl text-cocoa leading-snug">
                  {item.name}
                </h2>
                <div className="mt-1 text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">
                  {item.tagline}
                </div>
                <p className="mt-3 text-sm text-cocoa-soft leading-relaxed">
                  {item.description}
                </p>

                <div className="mt-5 flex items-center border-t border-cream-deep pt-4">
                  <SpiceLevel level={item.spice} />
                </div>
              </div>

              <span className="pointer-events-none absolute left-6 right-6 bottom-0 h-px bg-gradient-to-r from-transparent via-[#C8A14B] to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
            </article>
          ))}
        </div>
      </section>

      {/* CTA Strip */}
      <section className="bg-cocoa text-[#FAF3E7] py-16 md:py-20 grain">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4A24E] font-semibold">
            Catering & Bulk Orders
          </div>
          <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">
            Feeding a crowd? We&rsquo;ll handi the rest.
          </h3>
          <p className="mt-4 text-[#FAF3E7]/75 max-w-2xl mx-auto">
            From intimate dinners to corporate lunches and weddings — give us a
            24-hour heads up and pick your spice level.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/#contact"
              data-testid="menu-page-cta-contact"
              className="inline-flex items-center justify-center rounded-full bg-[#C8A14B] text-[#3A1B0E] hover:bg-[#D4A24E] hover:-translate-y-0.5 transition-all px-7 py-3.5 font-semibold shadow-md"
            >
              Enquire for Catering
            </Link>
            <Link
              to="/#locations"
              data-testid="menu-page-cta-locations"
              className="inline-flex items-center justify-center rounded-full border-2 border-[#C8A14B] text-[#FAF3E7] hover:bg-[#C8A14B] hover:text-[#3A1B0E] transition-all px-7 py-3 font-semibold"
            >
              Find a Kitchen
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
